import { isNil } from "client/Misc/utils";

export function getAuthority(absoluteUrl: string): string {
	const urlParts = absoluteUrl.split("/");
	return urlParts[0] + "//" + urlParts[2];
}

export function isAbsoluteUrl(url: string): boolean {
	const external = RegExp("^(https?:)?//", "i");
	return external.test(url);
}

function isScalar(value: unknown): value is null | undefined | boolean | number | string {
	return isNil(value)
		|| typeof value === "boolean"
		|| typeof value === "string"
		|| typeof value === "number";
};

type EncodeVisitor = {
	addEntry: (key: string, value: null | undefined | boolean | number | string) => void;
};

function encodeValue(
	path: string,
	data: unknown,
	visitor: EncodeVisitor
): void {
	if (data instanceof Date) {
		visitor.addEntry(path, data.toISOString());

	} else if (isScalar(data)) {
		visitor.addEntry(path, data);

	} else if (Array.isArray(data)) {
		for (let i = 0; i < data.length; i++) {
			const value: unknown = data[i];

			encodeValue(
				isScalar(value) || value instanceof Date
					? path
					: path + `[${i}]`,
				value,
				visitor
			);
		}

	} else if (typeof data === "object") {
		for (const key in data) {
			if (Object.prototype.hasOwnProperty.call(data, key)) {
				const prefix: string = path
					? path + "."
					: path;

				encodeValue(
					prefix + key,
					data[key as keyof typeof data],
					visitor
				);
			}
		}

	} else {
		throw new Error(`Not supported data type ${typeof data} for path '${path}' ('${data}')`);
	}
}

export function urlEncodeObject(data: object, shouldIgnoreNilValues: boolean = false): string {

	const entries: string[] = [];

	const visitor: EncodeVisitor = {
		addEntry: (
			key: string,
			value: null | undefined | boolean | number | string
		): void => {
			if (isNil(value)) {
				if (shouldIgnoreNilValues) {
					return;
				}

				value = "";
			}

			entries.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
		}
	};

	encodeValue(
		"",
		data,
		visitor
	);

	return entries.join("&");
}

export function updateSearchParameters(
	parameters: URLSearchParams,
	data: object,
	shouldIgnoreNilValues: boolean = false
): void {
	const visitor: EncodeVisitor = {
		addEntry: (
			key: string,
			value: null | undefined | boolean | number | string
		): void => {
			if (isNil(value)) {
				if (shouldIgnoreNilValues) {
					return;
				}

				value = "";
			} else if (value === true) {
				value = "true";
			} else if (value === false) {
				value = "false";
			} else {
				value = value.toString();
			}

			parameters.append(key, value);
		}
	};

	encodeValue(
		"",
		data,
		visitor
	);
}

export type QueryData<TQueryParameter extends string> = Partial<Record<TQueryParameter, string>>;

export function parseQueryString<TQueryParameter extends string>(
	queryString: string,
	parameters: readonly TQueryParameter[]
): TryResult<QueryData<TQueryParameter>> {
	if (queryString[0] === "?") {
		queryString = queryString.substring(1);
	}

	const queryData: string[][] = queryString.split("&")
		.map(p => p.split("=").map(decodeURIComponent));

	if (queryData.some(p => p.length !== 2)) {
		console.error(`Incorrect query string format.`);
		return { wasSuccessful: false };
	}

	const result: Partial<Record<TQueryParameter, string>> = {};
	for (const parameterName of parameters) {
		const parameter: string[] | undefined = queryData.find(p => p[0] === parameterName);
		if (parameter) {
			result[parameterName] = parameter[1];
		}
	}

	return {
		wasSuccessful: true,
		result
	};
}

export function generateQueryString<TQueryParameter extends string>(
	data: Partial<Record<TQueryParameter, string | readonly string[]>>
): string {
	return urlEncodeObject(data, true);
}

const SANITIZE_URI_VALUE = "javascript:void(0)";

export function sanitizeUri<TValue extends string | undefined | null>(input: TValue): TValue {
	if (!input) {
		return input;
	}

	const isJavascriptScheme: boolean = /^javascript:(.*)/i.exec(input.trim()) !== null;
	if (isJavascriptScheme) {
		return SANITIZE_URI_VALUE as TValue;
	}

	return input;
}

export function sanitizeUriForReact(input: string | nil): string | undefined {
	if (typeof input === "undefined" || input === null) {
		return undefined;
	}

	const result: string = sanitizeUri(input);
	if (result === SANITIZE_URI_VALUE) {
		return undefined;
	}
	return result;
}

